import { defaultRegion } from "common/config";
import React, { useState } from "react";

type CustomCoordinates = {
  latitude: number;
  longitude: number;
  accuracy?: number;
  altitude?: number | null;
  altitudeAccuracy?: number | null;
  heading?: number | null;
  speed?: number | null;
};

// Result type for requestUserLocation Promise
export type LocationResult = {
  success: boolean;
  location: CustomCoordinates;
  isUsingFallback: boolean;
};

export function useGeoLocation() {
  // Initialize with default location (London) so marker always shows
  const [location, setLocation] = useState<CustomCoordinates>({
    latitude: defaultRegion.latitude,
    longitude: defaultRegion.longitude,
    accuracy: 0,
    altitude: null,
    altitudeAccuracy: null,
    heading: null,
    speed: null,
  });
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  // Flag to indicate if using fallback location (GPS failed/denied/timeout)
  const [isUsingFallback, setIsUsingFallback] = React.useState(false);

  React.useEffect(() => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported.");
      setLoading(false);
      setIsUsingFallback(true);
      // location already has default value, no need to set again
      return;
    }
    console.log("Loading location....");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        console.log("Location Loaded");
        setLocation(position.coords);
        setLoading(false);
        setIsUsingFallback(false);
      },
      (err) => {
        console.log("Error while loading location", err);
        setError(err.message);
        setLoading(false);
        setIsUsingFallback(true);
        //if there is some error
        //or if user doesn't allow location
        //set location london
        setLocation({
          latitude: defaultRegion.latitude,
          longitude: defaultRegion.longitude,
          accuracy: 0,
          altitude: null,
          altitudeAccuracy: null,
          heading: null,
          speed: null,
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      },
    );
  }, []);

  const requestUserLocation = (): Promise<LocationResult> => {
    return new Promise((resolve) => {
      setLoading(true);
      setError(null);

      if (!navigator.geolocation) {
        setLoading(false);
        setError("Geolocation is not supported.");
        setIsUsingFallback(true);
        const fallbackLocation: CustomCoordinates = {
          latitude: defaultRegion.latitude,
          longitude: defaultRegion.longitude,
          accuracy: 0,
          altitude: null,
          altitudeAccuracy: null,
          heading: null,
          speed: null,
        };
        setLocation(fallbackLocation);
        resolve({
          success: false,
          location: fallbackLocation,
          isUsingFallback: true,
        });
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLoading(false);
          const coords: CustomCoordinates = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            altitudeAccuracy: position.coords.altitudeAccuracy,
            heading: position.coords.heading,
            speed: position.coords.speed,
          };
          setLocation(coords);
          setIsUsingFallback(false);
          resolve({
            success: true,
            location: coords,
            isUsingFallback: false,
          });
        },
        (err) => {
          setLoading(false);
          setError(err.message);
          setIsUsingFallback(true);
          const fallbackLocation: CustomCoordinates = {
            latitude: defaultRegion.latitude,
            longitude: defaultRegion.longitude,
            accuracy: 0,
            altitude: null,
            altitudeAccuracy: null,
            heading: null,
            speed: null,
          };
          setLocation(fallbackLocation);
          resolve({
            success: false,
            location: fallbackLocation,
            isUsingFallback: true,
          });
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0,
        },
      );
    });
  };
  return { location, error, loading, requestUserLocation, isUsingFallback };
}
